import styled, { css } from 'styled-components';
import trim from 'lodash/trim';
import AnonEyeIcon from '@paradigm/design-system/src/assets/AnonEyeIcon';
import { useDrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import useIsViewerPrime from '@paradigm/features/src/prime/hooks/useIsViewerPrime';
import Text from '@paradigm/design-system/src/components/Text';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import { Drfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import Tooltip from '@paradigm/design-system/src/components/Tooltip';
import PrimeTag from '@paradigm/design-system/src/assets/PrimeTag';
import { paradigmLightBlue } from '@paradigm/design-system/src/colors';
import { DeskName } from './common';

interface Props {
  'data-testid'?: string;
  anonDisplayType?: 'taker-profile' | 'anon-icon' | 'anon-label';
}

export default function TakerDesk({
  anonDisplayType = 'anon-label',
  ...props
}: Props) {
  const rfq = useDrfqRfq();
  const isPrimeUser = useIsViewerPrime();

  if (!rfq.is_taker_anonymous) {
    return (
      <DeskName aria-label="desk name" {...props}>
        {rfq.taker_desk_name}
      </DeskName>
    );
  }

  const shouldShowTakerProfile = isPrimeUser;

  if (shouldShowTakerProfile && anonDisplayType === 'taker-profile') {
    return (
      <FlexContainer aria-label="anon taker profile" {...props}>
        <AnonEyeIcon />
        <Tooltip
          id="taker-profile-formula"
          showIconInPopover={false}
          popoverCss={css`
            .popover-body {
              padding: 4px;
              padding-right: 8px;
            }
          `}
          tooltipContent={
            <TooltipContentWrapper>
              <PrimeTag width="16px" height="16px" />
              <Text size="small">
                <div>Taker Fill Rate = RFQ Notional</div>
                Volume Traded / Requested
              </Text>
            </TooltipContentWrapper>
          }
        >
          <TakerProfileText>
            {getTakerFillRateLabel(rfq.taker_profile)}
          </TakerProfileText>
        </Tooltip>
      </FlexContainer>
    );
  }

  if (shouldShowTakerProfile && anonDisplayType === 'anon-icon') {
    return (
      <FlexContainer aria-label="anon icon" {...props}>
        <AnonEyeIcon />
      </FlexContainer>
    );
  }

  return (
    <DeskName aria-label="anon desk name" {...props}>
      <AnonEyeIcon />
    </DeskName>
  );
}

const FlexContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 6px;
`;

const TooltipContentWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  column-gap: 6px;
`;

const TakerProfileText = styled(Text).attrs({ size: 'xsmall' })`
  opacity: 0.4;
  text-underline-offset: 6px;
  text-decoration-color: ${paradigmLightBlue};
  text-decoration-style: dashed;
  text-decoration-line: underline;

  &:hover {
    opacity: 0.7;
  }
`;

const getTakerFillRateLabel = (takerProfile: Drfq['taker_profile']) => {
  if (takerProfile == null) return NEGATIVE_SIGN;
  if (takerProfile.average_taker_fill_rate === 'NA') return NEGATIVE_SIGN;

  return trim(takerProfile.average_taker_fill_rate);
};
